import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useFormCloseGuard } from '../useFormCloseGuard'
import { useEntryDateFields } from './useEntryDateFields'
import { useEntryChoicesLoader } from './useEntryChoicesLoader'
import { useJournalDetailStore } from '@/views/apps/journals/store/journalDetailStore'
import { useJournalEntriesStore } from '@/views/apps/journals/store/journalEntriesStore'
import { useErrorHandler } from '@/views/apps/journals/composables/useErrorHandler'
import { journalsApi } from '@/views/apps/journals/api/journalsApi'
import {
  getFormKey,
  isMultiSelect,
  needsChoices,
} from '@/views/apps/journals/utils/attributeHelpers'
import { getFileNameFromPath, getFileUrl } from '@/views/apps/journals/utils/fileUtils'
import {
  formatDateForAPI,
  formatDateOnlyForAPI,
  parseDateFromInput,
  parseDateTimeValue,
} from '@/views/apps/journals/utils/dateUtils'
import type {
  AttributeValueType,
  JournalAttribute,
  JournalEntry,
} from '@/views/apps/journals/types/journal.types'

export interface EntryFormProps {
  isOpen: boolean
  journalId: number | null
  entryId?: number | null
  readOnly?: boolean
}

export interface EntryFormEmit {
  (e: 'update:isOpen', value: boolean): void
  (e: 'saved'): void
}

/** Состояние, загрузка, валидация и сохранение формы записи журнала. */
export function useEntryForm(props: EntryFormProps, emit: EntryFormEmit) {
  const detailStore = useJournalDetailStore()
  const entriesStore = useJournalEntriesStore()
  const { journalAttributes, isFetchingAttributes } = storeToRefs(detailStore)
  const { handleError, showSuccess } = useErrorHandler()

  const entryForm = ref<Record<string, AttributeValueType>>({})
  const fileFields = ref<Record<number, File | null>>({})
  const errors = ref<Record<string, string>>({})
  const isSubmitting = ref(false)
  const currentEntry = ref<JournalEntry | null>(null)
  const entryLoadedChoiceLabels = ref<Record<string, string>>({})
  const treeSelectedLabels = ref<Record<string, Record<string | number, string>>>({})
  const creationTemplateAttributesIds = ref<number[]>([])
  const editableTemplateAttributesIds = ref<number[]>([])
  const initialFormSnapshot = ref('')

  const dateFields = useEntryDateFields({ journalAttributes, entryForm, errors })
  const choices = useEntryChoicesLoader({
    getJournalId: () => props.journalId,
    journalAttributes,
    entryForm,
    errors,
    entryLoadedChoiceLabels,
    treeSelectedLabels,
  })

  const journalName = computed(() => detailStore.currentJournal?.name || '')

  /** Просмотр, если бэк не отдал ни одного редактируемого атрибута. */
  const isFormReadOnly = computed(() => {
    if (props.readOnly) {
      return true
    }

    if (!props.entryId) {
      return false
    }

    if (!currentEntry.value) {
      return true
    }

    return editableTemplateAttributesIds.value.length === 0
  })

  const modalTitle = computed(() => {
    if (isFormReadOnly.value && props.entryId) {
      return 'Просмотр записи'
    }

    return props.entryId ? 'Редактирование записи' : 'Создание записи'
  })

  const getFieldLabelBase = (attr: JournalAttribute): string => {
    const displayName = attr.short_name || attr.attribute_name
    const unit = attr.unit ? ` (${attr.unit})` : ''

    return `${displayName}${unit}`
  }

  const getFieldType = (attr: JournalAttribute): string => {
    if (needsChoices(attr)) {
      return 'select'
    }

    switch (attr.attribute_type) {
      case 'float':
      case 'integer':
        return 'number'
      case 'boolean':
        return 'checkbox'
      case 'date':
        return 'date'
      case 'date_time':
        return 'date_time'
      case 'time':
        return 'time'
      case 'file':
        return 'file'
      case 'text':
      default:
        return 'text'
    }
  }

  const getDefaultValue = (attr: JournalAttribute): AttributeValueType => {
    if (needsChoices(attr)) {
      if (isMultiSelect(attr)) {
        return [] as unknown as AttributeValueType
      }

      return undefined as unknown as AttributeValueType
    }

    if (attr.attribute_type === 'boolean') {
      return false
    }

    return ''
  }

  const visibleAttributes = computed(() =>
    journalAttributes.value.filter(attr => {
      if (attr.is_visible === false) {
        return false
      }

      if (!props.entryId) {
        return creationTemplateAttributesIds.value.includes(attr.id)
      }

      return true
    }),
  )

  const isAttributeReadOnly = (attr: JournalAttribute): boolean => {
    if (isFormReadOnly.value) {
      return true
    }

    if (!props.entryId) {
      return !creationTemplateAttributesIds.value.includes(attr.id)
    }

    return !editableTemplateAttributesIds.value.includes(attr.id)
  }

  /** Атрибуты, участвующие в текущем режиме (создание/редактирование). */
  const isAttributeInScope = (attr: JournalAttribute): boolean =>
    props.entryId
      ? editableTemplateAttributesIds.value.includes(attr.id)
      : creationTemplateAttributesIds.value.includes(attr.id)

  const onFieldChange = (formKey: string, value: AttributeValueType | null) => {
    entryForm.value[formKey] = (value ?? '') as AttributeValueType
    if (errors.value[formKey]) {
      delete errors.value[formKey]
    }
  }

  const onFileChange = (templateAttributeId: number, event: Event) => {
    const target = event.target as HTMLInputElement
    if (target.files && target.files.length > 0) {
      fileFields.value[templateAttributeId] = target.files[0]

      const attr = journalAttributes.value.find(a => a.id === templateAttributeId)
      if (attr && errors.value[getFormKey(attr)]) {
        delete errors.value[getFormKey(attr)]
      }
    } else {
      fileFields.value[templateAttributeId] = null
    }
  }

  const getFormSnapshot = (): string => {
    const fileNames: Record<number, string | null> = {}
    for (const [id, file] of Object.entries(fileFields.value)) {
      fileNames[Number(id)] = file?.name ?? null
    }

    return JSON.stringify({
      entryForm: entryForm.value,
      fileNames,
    })
  }

  const captureFormSnapshot = () => {
    initialFormSnapshot.value = getFormSnapshot()
  }

  const hasUnsavedChanges = (): boolean => {
    if (isFormReadOnly.value) {
      return false
    }

    return getFormSnapshot() !== initialFormSnapshot.value
  }

  const resetForm = () => {
    entryForm.value = {}
    fileFields.value = {}
    errors.value = {}
    currentEntry.value = null
    creationTemplateAttributesIds.value = []
    editableTemplateAttributesIds.value = []
    entryLoadedChoiceLabels.value = {}
    treeSelectedLabels.value = {}
    dateFields.resetDateFields()
    choices.resetChoices()

    journalAttributes.value.forEach(attr => {
      const key = getFormKey(attr)
      const defaultValue = getDefaultValue(attr)

      entryForm.value[key] = defaultValue
      if (attr.attribute_type === 'time') {
        dateFields.initTimeValue(key, defaultValue)
      }
    })
    dateFields.syncMaskedInputs()
    initialFormSnapshot.value = ''
  }

  const loadCreationAttributes = async () => {
    if (!props.journalId) {
      return
    }

    try {
      const response = await journalsApi.getCreationAttributesList(props.journalId)

      creationTemplateAttributesIds.value
        = response.creation_template_attributes_ids || []
      choices.preloadSingleSelectChoices(creationTemplateAttributesIds.value)
    } catch (error) {
      handleError(error, 'Ошибка загрузки списка доступных атрибутов')
    }
  }

  const applyChoiceValue = (
    attr: JournalAttribute,
    formKey: string,
    rawValue: AttributeValueType,
  ) => {
    if (isMultiSelect(attr) && Array.isArray(rawValue)) {
      const items = rawValue as Array<{ id: number | string; name?: string }>
      entryForm.value[formKey] = items.map(i => i.id) as unknown as AttributeValueType
      treeSelectedLabels.value[formKey] = {}
      items.forEach(i => {
        if (i.name) {
          treeSelectedLabels.value[formKey][i.id] = i.name
        }
      })
    } else if (typeof rawValue === 'object' && rawValue !== null && 'id' in rawValue) {
      const obj = rawValue as { id: number | string; name?: string }
      entryForm.value[formKey] = obj.id as AttributeValueType
      if (obj.name != null) {
        entryLoadedChoiceLabels.value[formKey] = String(obj.name)
      }
    } else {
      entryForm.value[formKey] = rawValue
    }
  }

  const applyDateValue = (formKey: string, rawValue: AttributeValueType) => {
    if (!rawValue) {
      entryForm.value[formKey] = ''

      return
    }

    try {
      let date: Date | null = null
      if (typeof rawValue === 'string') {
        const str = rawValue.trim()
        if (/^\d{2}\.\d{2}\.\d{4}$/.test(str)) {
          date = parseDateFromInput(str)
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
          date = new Date(`${str}T00:00:00`)
        } else {
          date = new Date(str)
        }
      } else if (rawValue instanceof Date) {
        date = rawValue
      }

      entryForm.value[formKey] = date && !Number.isNaN(date.getTime())
        ? formatDateOnlyForAPI(date)
        : ''
    } catch {
      entryForm.value[formKey] = ''
    }
  }

  const applyBooleanValue = (formKey: string, rawValue: AttributeValueType) => {
    if (rawValue === true || rawValue === 1) {
      entryForm.value[formKey] = true
    } else if (rawValue === false || rawValue === 0) {
      entryForm.value[formKey] = false
    } else if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase()
      entryForm.value[formKey] = ['true', '1', 'yes', 'y', 'да', 'д'].includes(normalized)
    } else {
      entryForm.value[formKey] = false
    }
  }

  const applyEntryValue = (attr: JournalAttribute, rawValue: AttributeValueType) => {
    const formKey = getFormKey(attr)

    if (attr.attribute_type === 'file') {
      entryForm.value[formKey] = rawValue

      return
    }

    if (needsChoices(attr)) {
      applyChoiceValue(attr, formKey, rawValue)

      return
    }

    if (attr.choices && attr.choices.length > 0) {
      entryForm.value[formKey]
        = typeof rawValue === 'object' && rawValue !== null && 'id' in rawValue
          ? (rawValue as { id: number }).id
          : rawValue

      return
    }

    if (attr.attribute_type === 'date_time') {
      if (rawValue && typeof rawValue === 'string') {
        const parsed = parseDateTimeValue(rawValue)
        entryForm.value[formKey]
          = parsed && !Number.isNaN(parsed.getTime()) ? formatDateForAPI(parsed) : ''
      } else {
        entryForm.value[formKey] = rawValue
      }

      return
    }

    if (attr.attribute_type === 'time') {
      entryForm.value[formKey] = rawValue
      dateFields.initTimeValue(formKey, rawValue)

      return
    }

    if (attr.attribute_type === 'date') {
      applyDateValue(formKey, rawValue)

      return
    }

    if (attr.attribute_type === 'boolean') {
      applyBooleanValue(formKey, rawValue)

      return
    }

    entryForm.value[formKey] = rawValue
  }

  const loadEntryData = async () => {
    if (!props.entryId || !props.journalId) {
      return
    }

    try {
      const entry = await journalsApi.getEntry(props.journalId, props.entryId)

      currentEntry.value = entry
      editableTemplateAttributesIds.value
        = entry.editable_template_attributes_ids || []
      choices.preloadSingleSelectChoices(editableTemplateAttributesIds.value)

      if (Array.isArray(entry.attribute_values)) {
        entry.attribute_values.forEach(attrValue => {
          const attr = journalAttributes.value.find(
            a => a.id === attrValue.template_attribute_id,
          )
          if (attr) {
            applyEntryValue(attr, attrValue.value)
          }
        })
        dateFields.syncMaskedInputs()
      }
    } catch (error) {
      handleError(error, 'Ошибка загрузки записи')
    }
  }

  const validateForm = (): boolean => {
    errors.value = {}
    let isValid = true

    journalAttributes.value.forEach(attr => {
      if (!isAttributeInScope(attr)) {
        return
      }

      const key = getFormKey(attr)
      if (attr.attribute_type === 'file') {
        if (attr.required && !fileFields.value[attr.id]) {
          errors.value[key] = 'Необходимо выбрать файл'
          isValid = false
        }

        return
      }

      const value = entryForm.value[key]
      const isEmpty = Array.isArray(value) ? value.length === 0 : (!value || value === '')

      if (attr.required && isEmpty) {
        errors.value[key] = 'Поле обязательно для заполнения'
        isValid = false
      }

      if (
        value
        && (attr.attribute_type === 'float' || attr.attribute_type === 'integer')
        && Number.isNaN(Number(value))
      ) {
        errors.value[key] = 'Значение должно быть числом'
        isValid = false
      }
    })

    return isValid
  }

  const buildAttributeValue = (
    attr: JournalAttribute,
    value: AttributeValueType,
  ): { template_attribute_id: number; value: AttributeValueType } | null => {
    if (needsChoices(attr)) {
      if (isMultiSelect(attr) && Array.isArray(value)) {
        return {
          template_attribute_id: attr.id,
          value: value.map(id => ({ id })) as AttributeValueType,
        }
      }

      return { template_attribute_id: attr.id, value: { id: value } as AttributeValueType }
    }

    if (attr.choices && attr.choices.length > 0) {
      return { template_attribute_id: attr.id, value: { id: value } as AttributeValueType }
    }

    if (attr.attribute_type === 'date') {
      if (value && typeof value === 'string') {
        const date = new Date(`${value}T00:00:00`)
        if (!Number.isNaN(date.getTime())) {
          return { template_attribute_id: attr.id, value: formatDateOnlyForAPI(date) }
        }
      }

      return null
    }

    return { template_attribute_id: attr.id, value }
  }

  const collectPayload = () => {
    const attributeValues: Array<{ template_attribute_id: number; value: AttributeValueType }> = []
    const filesToUpload: Record<number, File> = {}

    journalAttributes.value.forEach(attr => {
      if (!isAttributeInScope(attr)) {
        return
      }

      const key = getFormKey(attr)
      if (attr.attribute_type === 'file') {
        const file = fileFields.value[attr.id]
        if (file) {
          filesToUpload[attr.id] = file
        }

        return
      }

      const value = entryForm.value[key]
      const isEmpty = Array.isArray(value)
        ? value.length === 0
        : value === '' || value === null || value === undefined
      if (isEmpty) {
        return
      }

      const built = buildAttributeValue(attr, value)
      if (built) {
        attributeValues.push(built)
      }
    })

    return { attributeValues, filesToUpload }
  }

  const forceClose = () => {
    resetForm()
    emit('update:isOpen', false)
  }

  const {
    showUnsavedChangesDialog,
    requestClose,
    handleCloseConfirm,
    onDialogDismissAttempt,
    onDialogUpdate,
  } = useFormCloseGuard({
    hasUnsavedChanges,
    forceClose,
    enabled: () => !isFormReadOnly.value,
  })

  const saveEntry = async () => {
    if (isFormReadOnly.value || !validateForm()) {
      return
    }

    isSubmitting.value = true

    try {
      const { attributeValues, filesToUpload } = collectPayload()
      const entryData = { attribute_values: attributeValues }
      const hasFiles = Object.keys(filesToUpload).length > 0

      if (props.entryId) {
        const promises: Promise<JournalEntry>[] = []

        if (attributeValues.length > 0) {
          promises.push(entriesStore.updateJournalEntry(props.journalId!, props.entryId, entryData))
        }
        if (hasFiles) {
          promises.push(journalsApi.attachFilesToEntry(props.journalId!, props.entryId, filesToUpload))
        }

        if (promises.length > 0) {
          const results = await Promise.all(promises)
          currentEntry.value = results[results.length - 1]
        }

        showSuccess('Запись успешно обновлена')
      } else {
        const newEntry = await entriesStore.createJournalEntry(props.journalId!, entryData)
        currentEntry.value = newEntry

        if (hasFiles) {
          currentEntry.value = await journalsApi.attachFilesToEntry(
            props.journalId!,
            newEntry.id,
            filesToUpload,
          )
        }

        showSuccess('Запись успешно создана')
      }

      emit('saved')
      forceClose()
    } catch (error) {
      handleError(error, 'Ошибка при сохранении записи')
    } finally {
      isSubmitting.value = false
    }
  }

  watch(
    () => props.isOpen,
    async isOpen => {
      if (!isOpen || !props.journalId) {
        return
      }

      if (
        !detailStore.currentJournal
        || detailStore.currentJournal.id !== props.journalId
        || !journalAttributes.value.length
      ) {
        await detailStore.fetchJournal(props.journalId)
      }

      resetForm()

      if (!props.entryId) {
        await loadCreationAttributes()
      } else {
        await loadEntryData()
      }

      captureFormSnapshot()
    },
  )

  return {
    ...dateFields,
    ...choices,
    forceClose,
    requestClose,
    handleCloseConfirm,
    onDialogDismissAttempt,
    onDialogUpdate,
    showUnsavedChangesDialog,
    currentEntry,
    entryForm,
    errors,
    fileFields,
    getFieldLabelBase,
    getFieldType,
    getFileNameFromPath,
    getFileUrl,
    getFormKey,
    isAttributeReadOnly,
    isFormReadOnly,
    isFetchingAttributes,
    isMultiSelect,
    isSubmitting,
    journalAttributes,
    journalName,
    modalTitle,
    onFieldChange,
    onFileChange,
    saveEntry,
    visibleAttributes,
  }
}
